/**
 * Die Themenwahl — hell, dunkel oder das, was das Gerät sagt.
 *
 * Drei Zustände, nicht zwei: „System" ist kein dritter Anstrich, sondern die
 * Abwesenheit einer Wahl. Deshalb wird dabei sowohl der Speichereintrag als
 * auch das Attribut ENTFERNT; nur so greift die Medienabfrage in tokens.css
 * wieder.
 *
 * Gesetzt wird das Attribut zusätzlich vor dem ersten Malen von
 * `public/theme-init.js` — diese Datei hier ist der Schalter zur Laufzeit.
 */
export type Theme = 'system' | 'light' | 'dark'

const SCHLUESSEL = 'famora-theme'

export function leseTheme(): Theme {
  try {
    const wert = localStorage.getItem(SCHLUESSEL)
    return wert === 'light' || wert === 'dark' ? wert : 'system'
  } catch {
    // Ein gesperrter Speicher ist kein Grund, die App nicht zu zeigen.
    return 'system'
  }
}

/** Die WAHL festhalten — sofort, unabhängig von jeder Darstellung. */
function merken(theme: Theme): void {
  try {
    if (theme === 'system') localStorage.removeItem(SCHLUESSEL)
    else localStorage.setItem(SCHLUESSEL, theme)
  } catch {
    // Ein gesperrter Speicher ist kein Grund, die Wahl abzulehnen - sie gilt
    // dann nur für diese Sitzung.
  }
}

/** Die DARSTELLUNG umstellen. */
function anwenden(theme: Theme): void {
  const wurzel = document.documentElement
  if (theme === 'system') delete wurzel.dataset.theme
  else wurzel.dataset.theme = theme
}

/**
 * Setzt das Theme und zieht es als Kreis über die Seite auf.
 *
 * Der Kreis startet dort, wo geklickt wurde, und wächst bis zur entferntesten
 * Ecke. Fehlt die Schnittstelle oder will die Person weniger Bewegung, wird
 * schlicht umgeschaltet — die Wahl darf nie an der Verzierung scheitern.
 */
export function setzeTheme(theme: Theme, ursprung?: { x: number; y: number }): void {
  // ⚠️ Erst merken, dann zeigen. Der Kreis läuft im Rückruf von
  // `startViewTransition`, also einen Frame später - wer die Seite in diesem
  // Moment verlässt, hätte seine Wahl sonst verloren.
  merken(theme)

  // `startViewTransition` steht im DOM-Typ, aber nicht in jedem Browser -
  // deshalb die Abfrage zur Laufzeit statt eines eigenen Typs.
  const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (typeof document.startViewTransition !== 'function' || ruhig || !ursprung) {
    anwenden(theme)
    return
  }

  const { x, y } = ursprung
  const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y))
  document.documentElement.classList.add('theme-vt')
  const uebergang = document.startViewTransition(() => {
    anwenden(theme)
  })
  void uebergang.ready
    .then(
      () =>
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
          {
            duration: 620,
            easing: 'cubic-bezier(.2, 0, 0, 1)',
            pseudoElement: '::view-transition-new(root)',
          },
        ).finished,
    )
    .catch(() => undefined)
    .finally(() => {
      document.documentElement.classList.remove('theme-vt')
    })
}
